import type { ReactNode } from 'react'
import { masteryColor } from '../lib/analytics'

export function Card({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={`card p-5 ${className}`}>{children}</div>
}

export function SectionTitle({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-ink">{title}</h2>
        {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

export function StatCard({
  label,
  value,
  icon,
  tint,
  delta,
}: {
  label: string
  value: string
  icon: ReactNode
  tint: string
  delta?: { value: number; suffix?: string }
}) {
  return (
    <div className="card flex items-center gap-4 p-5">
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
        style={{ backgroundColor: `${tint}1f`, color: tint }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold tracking-tight text-ink">{value}</span>
          {delta && (
            <span
              className={`text-xs font-semibold ${
                delta.value >= 0 ? 'text-info' : 'text-muted'
              }`}
            >
              {delta.value >= 0 ? '▲' : '▼'} {Math.abs(delta.value)}
              {delta.suffix ?? ''}
            </span>
          )}
        </div>
        <p className="truncate text-sm text-muted">{label}</p>
      </div>
    </div>
  )
}

export function MasteryBar({
  value,
  width = 120,
  showLabel = true,
}: {
  value: number
  width?: number | string
  showLabel?: boolean
}) {
  return (
    <div className="flex flex-1 items-center gap-2">
      <div
        className="h-2 flex-1 overflow-hidden rounded-full bg-raised"
        style={{ width }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${value}%`, backgroundColor: masteryColor(value) }}
        />
      </div>
      {showLabel && (
        <span className="w-9 text-right text-xs font-semibold tabular-nums text-muted">
          {value}%
        </span>
      )}
    </div>
  )
}

export function Pill({
  children,
  color,
  soft = true,
}: {
  children: ReactNode
  color: string
  soft?: boolean
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={
        soft
          ? { backgroundColor: `${color}1f`, color }
          : { backgroundColor: color, color: '#fff' }
      }
    >
      {children}
    </span>
  )
}

export function TrendBadge({ value }: { value: number }) {
  const up = value >= 0
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
        up ? 'text-info' : 'text-muted'
      }`}
    >
      {up ? '▲' : '▼'} {Math.abs(value)}
    </span>
  )
}
